import { AddCircleOutline, Edit, RemoveCircleOutline, TrendingFlat } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { Wallet } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import WalletHistory from '../../components/History/WalletHistory';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import EditWallet from './EditWallet';
import EventModal from './EventModal';

type Row = DbDocument<Wallet>;

const WalletsList = () => {
  const { lang } = useLocale();
  const { wallets: allWallets, worker } = useCafe();
  const { getCurrency } = useAdmin();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [eventType, setEventType] = useState<'income' | 'spending' | 'transfer' | null>(null);
  const [entity, setEntity] = useState<Row | null>(null);

  const wallets = useMemo(
    () => (worker?.credentials.view_hidden_wallets ? allWallets : allWallets.filter((w) => !w.hidden)),
    [allWallets, worker?.credentials.view_hidden_wallets]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => <Typography>{r.name}</Typography>,
      },
      {
        name: 'balance',
        title: t.balance[lang],
        align: 'center',
        render: (r: Row) => <Typography>{getCurrency(r.balance)}</Typography>,
      },
      {
        name: 'history',
        title: t.history[lang],
        align: 'center',
        render: (r: Row) => <WalletHistory wallet={r} />,
      },
      {
        name: 'action',
        title: t.operation[lang],
        align: 'center',
        render: (r: Row) => (
          <Box display="flex" justifyContent="center" gap={1}>
            <IconButton
              color="success"
              onClick={() => {
                setEntity(r);
                setEventType('income');
              }}
              disabled={!worker?.credentials.perform_operations_with_wallets}
            >
              <AddCircleOutline />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => {
                setEntity(r);
                setEventType('spending');
              }}
              disabled={!worker?.credentials.perform_operations_with_wallets}
            >
              <RemoveCircleOutline />
            </IconButton>
            <IconButton
              color="info"
              onClick={() => {
                setEntity(r);
                setEventType('transfer');
              }}
              disabled={!worker?.credentials.perform_operations_with_wallets}
            >
              <TrendingFlat />
            </IconButton>
          </Box>
        ),
      },
      {
        name: 'edit',
        title: t.edit[lang],
        render: (r: Row) => (
          <IconButton
            onClick={() => {
              setEntity(r);
              setIsEditOpen(true);
            }}
            disabled={!worker?.credentials.edit_wallets}
          >
            <Edit />
          </IconButton>
        ),
      },
    ],
    [getCurrency, lang, worker?.credentials.edit_wallets, worker?.credentials.perform_operations_with_wallets]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto' }}>
      <DataTable
        columns={columns}
        rows={wallets}
        dataTestIdPrefix="wallet"
        actionButton={
          <PrimaryButton
            onClick={() => setIsEditOpen(true)}
            variant="contained"
            disabled={!worker?.credentials.edit_wallets}
          >
            {t.add_wallet[lang]}
          </PrimaryButton>
        }
      />

      {isEditOpen && (
        <EditWallet
          entity={entity}
          open={isEditOpen}
          onClose={() => {
            setIsEditOpen(false);
            setEntity(null);
          }}
        />
      )}

      {eventType && entity && (
        <EventModal
          wallet={entity}
          type={eventType}
          onClose={() => {
            setEntity(null);
            setEventType(null);
          }}
        />
      )}
    </Box>
  );
};

export default WalletsList;
